import React, { ChangeEvent, useState } from 'react'
import {
  Button,
  LabeledInput,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooterAction,
  ModalHeader,
  ModalOverlay
} from '@apex/component-library'
import { Box, Flex, Stack, Text, VStack } from '@chakra-ui/react'
import { SelectedTransfer } from '../../containers/ScheduledTransfers/ScheduledTransfers'
import { numberInputFormatter, useFormatNumber } from '../../utils'
import useUpdateScheduledTransfer from '../../api/useUpdateScheduledTransfer'
import { FormatNumberOptions } from '@formatjs/intl'

const createInitialAmountDisplay = (
  amount: number,
  formatter: (num: number, options?: FormatNumberOptions | undefined) => string
) => {
  const formattedAmount = numberInputFormatter(String(amount), formatter).display
  const cents = formattedAmount.split('.')[1]

  if (!cents) {
    return formattedAmount + '.00'
  }
  if (cents.length === 1) {
    return formattedAmount + '0'
  }
  return formattedAmount
}

export interface UpdateTransferScheduleModalProps {
  selectedTransfer: SelectedTransfer
  onClose: () => void
  refetch: () => void
}

export default function UpdateTransferScheduleModal({
  selectedTransfer: { id, amount: initialAmount, direction, schedule, type },
  onClose,
  refetch
}: UpdateTransferScheduleModalProps) {
  const numberFormatter = useFormatNumber()
  const [transferAmount, setTransferAmount] = useState(() => ({
    error: '',
    raw: initialAmount,
    display: createInitialAmountDisplay(initialAmount, numberFormatter)
  }))

  const { mutate, isLoading } = useUpdateScheduledTransfer()

  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formattedAmount = numberInputFormatter(event.target.value, numberFormatter)

    if (formattedAmount.display.length > 21) {
      return // max of 16 digits + commas
    }

    let error = ''
    if (Number(formattedAmount.raw) < 0.01) {
      error = 'Amount must be greater than $0.00'
    }
    setTransferAmount({
      error,
      raw: Number(formattedAmount.raw),
      display: formattedAmount.display
    })
  }

  const handelUpdateFinished = () => {
    onClose()
    refetch()
  }

  const onSaveTransferAmount = () =>
    mutate(
      { payload: { amount: transferAmount.raw }, direction, type, id },
      { onSettled: () => handelUpdateFinished() }
    )

  return (
    <Modal isCentered isOpen={true} onClose={onClose}>
      <ModalOverlay>
        <ModalContent w='100%' maxW='550px'>
          <ModalHeader textStyle='h1'>Edit Transfer Schedule Amount</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack pb={8} spacing={8}>
              <Text>To update other parameters, cancel the schedule and create a new one.</Text>
              <Flex>
                <VStack flexBasis='100%' align='start' spacing={3}>
                  <Text>Schedule</Text>
                  <Text fontWeight='medium'>{schedule}</Text>
                </VStack>

                <LabeledInput
                  flexBasis='100%'
                  label='Transfer Amount'
                  isRequired
                  isInvalid={!!transferAmount.error}
                  errorMessage={transferAmount.error}
                  iconLeft='$'
                  value={transferAmount.display}
                  onChange={onAmountChange}
                />
              </Flex>
            </Stack>
          </ModalBody>

          <ModalFooterAction>
            <Flex>
              <Box ml='auto'>
                <Button variant='white' aria-label='close' onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant='primary'
                  ml={3}
                  disabled={!!transferAmount.error || transferAmount.raw === initialAmount}
                  isLoading={isLoading}
                  onClick={onSaveTransferAmount}
                >
                  Save Change
                </Button>
              </Box>
            </Flex>
          </ModalFooterAction>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
